// The last focus colours each rig/frac attribute actually resolved to, so a
// surface can keep showing them while the next set is on its way.
//
// This is deliberately one global store rather than a ref per hook instance.
// The colours are a property of the well focus, not of any one surface, and the
// surfaces that read them mount and unmount independently: the map lives with
// the map, the details panel with the panel, and a chart's legend and its bars
// call the same hook twice. With per-instance memory, whichever surface mounted
// after the colours resolved would have nothing to hold and would paint
// something different from its neighbours, which is exactly the disagreement
// the focus sync exists to remove. One store keyed by attribute means they
// cannot answer differently.
//
// Written from an effect (never during render) by useFocusColorSync, once a
// resolution is genuinely active. Session-only: nothing here is persisted, and
// reset() clears it for a sign-out or org switch.
import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";

import type { IColorPalette } from "models/colorPalette";

export interface HeldFocusColors {
  // normalized match key -> hex, from the focus bins.
  colors: ReadonlyMap<string, string>;
  // normalized hexes the focus bins consume, matched or not.
  reservedColors: ReadonlySet<string>;
  // The well palette these colours were spread against.
  basePalette?: Maybe<IColorPalette>;
  // normalized match key -> focus bin label, bin label -> hex, whether the
  // focus has an Others row, and the focus Others colour.
  binOfKey?: ReadonlyMap<string, string>;
  binColors?: ReadonlyMap<string, string>;
  hasOthers?: boolean;
  othersColor?: string;
}

/**
 * Which focus property the wells legend currently on screen was built for.
 *
 * The legend arrives with a transaction id and nothing else identifying the
 * field it describes, so the only way to know it has gone stale is to note the
 * property in effect when it landed. Changing the focus updates the property
 * immediately but the legend keeps its old bins until a refetch lands, and that
 * refetch is debounced, so for a moment the app holds last field's bins under
 * this field's name. Treating that as a real answer paints colours from the
 * wrong field.
 */
export interface ObservedLegend {
  transactionId: string;
  property: Maybe<string>;
}

interface FocusColorHoldStore {
  // rig/frac attribute ("operator", "basin") -> the last colours it resolved to.
  heldByAttribute: Record<string, HeldFocusColors>;
  observedLegend: Maybe<ObservedLegend>;
  rememberFocusColors: (attribute: string, held: HeldFocusColors) => void;
  rememberLegendObservation: (transactionId: string, property: Maybe<string>) => void;
  reset: () => void;
}

const initialState: Pick<FocusColorHoldStore, "heldByAttribute" | "observedLegend"> = {
  heldByAttribute: {},
  observedLegend: undefined
};

// Every mounted consumer of useFocusColorSync writes the same answer on the
// same commit, each with its own freshly built Map. Comparing contents (not
// identity) keeps all but the first write from re-rendering the surfaces that
// read this store. The maps are legend-sized, so this is a handful of string
// comparisons.
function isSameHeld(a: Maybe<HeldFocusColors>, b: HeldFocusColors): boolean {
  if (!a) {
    return false;
  }
  if (
    a.basePalette?.id !== b.basePalette?.id ||
    a.hasOthers !== b.hasOthers ||
    a.othersColor !== b.othersColor ||
    a.colors.size !== b.colors.size ||
    a.reservedColors.size !== b.reservedColors.size ||
    (a.binOfKey?.size ?? 0) !== (b.binOfKey?.size ?? 0) ||
    (a.binColors?.size ?? 0) !== (b.binColors?.size ?? 0)
  ) {
    return false;
  }
  for (const [key, color] of b.colors) {
    if (a.colors.get(key) !== color) {
      return false;
    }
  }
  for (const [key, label] of b.binOfKey ?? []) {
    if (a.binOfKey?.get(key) !== label) {
      return false;
    }
  }
  for (const [label, color] of b.binColors ?? []) {
    if (a.binColors?.get(label) !== color) {
      return false;
    }
  }
  for (const color of b.reservedColors) {
    if (!a.reservedColors.has(color)) {
      return false;
    }
  }
  return true;
}

export const useFocusColorHoldStore = create<FocusColorHoldStore>()(
  subscribeWithSelector((set, get) => ({
    ...initialState,
    rememberFocusColors: (attribute, held) => {
      if (isSameHeld(get().heldByAttribute[attribute], held)) {
        return;
      }
      set((state) => ({
        heldByAttribute: { ...state.heldByAttribute, [attribute]: held }
      }));
    },
    // Recorded only when the legend itself changes, never when the focus does:
    // a focus change is precisely the moment the legend on screen stops
    // describing the current property, and overwriting the note then would
    // declare the stale legend fresh.
    rememberLegendObservation: (transactionId, property) => {
      const observed = get().observedLegend;
      if (observed?.transactionId === transactionId) {
        return;
      }
      set({ observedLegend: { transactionId, property } });
    },
    reset: () => set(initialState)
  }))
);

export const selectHeldFocusColors = (state: FocusColorHoldStore) =>
  state.heldByAttribute;

export const selectObservedLegend = (state: FocusColorHoldStore) => state.observedLegend;

export const selectRememberFocusColors = (state: FocusColorHoldStore) =>
  state.rememberFocusColors;

export const selectRememberLegendObservation = (state: FocusColorHoldStore) =>
  state.rememberLegendObservation;
